import {
  combineLatest,
  Observable,
  of,
  ReplaySubject,
} from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { UsersResource } from '../resources/users.resource';
import { UserNameService } from './user-name.service';
import { RoleCode, RolesResource } from '../resources/roles.resource';

export interface UserInfo {
  login: string;
  roleId: number;
  roleName: string;
  roleCode: RoleCode;
  external: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class UserInfoService {
  private userName: string | null;

  private userInfoSubject: ReplaySubject<UserInfo | null>;

  /**
   * Has no initial value (will try fetching UserInfo from server during service initialization).
   * The value of null is returned ONLY when either service failed to load UserInfo from server
   * or user login is set to null.
   */
  readonly userInfo$: Observable<UserInfo | null>;

  constructor(
    private userResource: UsersResource,
    private rolesResource: RolesResource,
    private userNameService: UserNameService,
  ) {
    this.userInfoSubject = new ReplaySubject<UserInfo | null>(1);
    this.userInfo$ = this.userInfoSubject.asObservable();
    this.userName = null;

    this.userNameService.userName$.subscribe((userName) => {
      this.userName = userName;
      this.reloadUserInfo();
    });
  }

  reloadUserInfo(): void {
    if (this.userName != null) {
      this.userResource.getItem(this.userName).pipe(
        switchMap((user) => combineLatest([of(user), this.rolesResource.getItem(user._links.role.id)])),
      ).subscribe(
        (data) => {
          const user = data[0];
          const role = data[1];
          console.log('Loading UserInfo from server was successful');
          this.userInfoSubject.next({
            login: user.login,
            roleId: role.id,
            roleName: role.name,
            roleCode: role.code,
            external: !!user.external,
          });
        },
        (error) => {
          console.error('Failed to load UserInfo from server', error);
          this.userInfoSubject.next(null);
        },
      );
    } else {
      console.warn('UserName is missing, skipping loading UserInfo');
      this.userInfoSubject.next(null);
    }
  }
}
